import { identity, } from 'ramda';
import { UNAUTHORIZED, } from 'http-status-codes';
import axios, {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
} from 'axios';

import BaseRequester from './BaseRequester';
import { API_URL, CHANNEL } from 'common/constants';
import { unauthorizedObserver } from 'common/observers';
import {
    Requester as RequesterInterface,
    RequesterOptions,
    RequesterRequestOptions,
} from './types';

const axiosRequester = axios.create({
    baseURL: `${API_URL}/`,
    withCredentials: true,
    headers: {
        'Channel': CHANNEL,
    },
});

const handleErrors = (error: any) => {
    // Line with checking me in address is the hack, probably you'll have to remove logic in Auth component.
    if (error.response.status === UNAUTHORIZED && !error.config.url.includes('me')) {
        unauthorizedObserver.broadcast({ error: true, code: UNAUTHORIZED, });
    }

    return Promise.reject(error);
};


axiosRequester.interceptors.response.use(
    identity,
    handleErrors
);

class Requester extends BaseRequester<AxiosInstance> implements RequesterInterface<Promise<AxiosResponse>> {
    constructor({
        requester = axiosRequester,
        ...params
    }: RequesterOptions = {}) {
        super({ requester, ...params });
    }

    get<T>(param1: string | RequesterRequestOptions, param2?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        const requester = this.requester.get;
        const url = this.prepareUrlWithVersion(param1);

        if (typeof param1 === 'string' && typeof param2 === 'object') {
            const config = {
                ...param2,
                ...this.getTransformers(param2),
            };

            return requester(url, config);
        }

        if (typeof param1 === 'object') {
            const config = {
                ...param1.config,
                ...this.getTransformers(param1.config)
            };

            return requester(url, config);
        }

        const config = {
            ...param2,
            ...this.getTransformers(param2),
        };

        return requester(url, config);
    }

    post<T>(
        arg: string | RequesterRequestOptions & { data: unknown },
        data?: unknown,
        config?: AxiosRequestConfig,
    ): Promise<AxiosResponse<T>> {
        const requester = this.requester.post;
        const url = this.prepareUrlWithVersion(arg);

        if (typeof arg === 'object') {
            const config = {
                ...arg.config,
                ...this.getTransformers(arg.config),
            };

            return requester(url, arg.data, config);
        }

        const finalConfig = {
            ...config,
            ...this.getTransformers(config),
        };

        return requester(url, data, finalConfig);
    }

    delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        // Simple implementation of delete action. Is no need for now implement more complex abstract layer.
        return this.requester.delete(url, config);
    }

    put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
        // Simple implementation of put action. Is no need for now implement more complex abstract layer.
        return this.requester.put(url, data, config);
    }
}

export default Requester;
